/**
 * 이상한 방 - 매치 세션의 페이즈 진입 처리 (`phaseEnter` 하나 → 자막 · 시계 잠금 · 전환 · 리빌 · 결과)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.4절(원래 방)
 *            `이상한방-상태모델-v0.1.md` 11.2절(클라이언트가 가진 것) · 11.3절(클라이언트가 해도 되는 것)
 *            로드맵 M2(매치 세션) · M4 준비 몫(`matchSessionEffects.ts` 400줄 분할 - 동작 변화 0)
 *
 * `matchSessionEffects.ts`에서 갈라져 나왔다. 싱글의 `session.onPhaseEnter`에 해당하는 자리다. 원칙도 같다 -
 * **규칙이 하나도 없다.** 어느 페이즈인지는 호스트가 `phaseEnter`로 말하고, 여기는 그 페이즈의 첫 화면을 세울 뿐이다.
 *
 * ## 왜 페이즈만 따로 떼었는가
 *
 * 효과 번역기(`apply`)의 나머지는 효과 하나가 화면 하나로 끝나는 **한 줄짜리 대응**이다. 페이즈 진입만은 다르다 -
 * 자막 · 카메라 · 프록시 · 곡 · 전환 · 리빌 · 결과가 **순서로 서로를 붙잡고** 있고, 그 순서가 곧 주석의 대부분이다.
 * 라운드 수명에 걸친 상태(수색 시작 시각 · 전환의 교체 소유권)도 전부 여기서만 쓰인다. 그래서 자른 이음매가 여기다.
 *
 * ## 수색 진입의 방 교체는 전환 이펙트가 가져간다
 *
 * 싱글과 같은 규약이다(`session.ts` 머리말 "외우기 → 수색 전환은 원본 방 위에서 돈다"). `phaseEnter{hunt}` 다음에 오는
 * `warpTo{false}` **한 번만** 전환이 소비하고(`transitionOwnsWarp` → `consumeTransitionWarp`), 그 뒤의 `warpTo`(원래 방
 * 왕복)는 디졸브를 탄다. 호스트는 `rooms{mutated}`를 그 `step`보다 **먼저** 보내므로(`matchHost.broadcast`) 전환의 교체
 * 콜백이 불릴 때 변경본은 이미 손에 있다. 소비하는 쪽(`warpTo` 분기)은 `matchSessionEffects.ts`에 남아 있다 - 소유권의
 * 세움과 소비가 두 파일에 갈린 것이 아니라, 세움·놓음(`resetTransition`)은 전부 여기고 효과 번역기는 **물어보기만** 한다.
 *
 * ## 바꾸기 진입은 "여기가 네 구획이다" 한 줄이다 (M11)
 *
 * 전원이 동시에 자기 구획을 고치므로 바꾸기의 첫 질문은 "언제 끝나나"가 아니라 **"어디까지가 내 것인가"**다.
 * 그 답이 토스트 한 줄(`zone.assigned`)이고 씬의 옅은 테두리(`app/stageChanger.ts`)가 같은 말을 그림으로 한다.
 * 자막판(`juice.phase`)을 쓰지 않는 것은 그것이 시계를 잡기 때문이다 - 60초짜리 페이즈에서 첫 1초를 자막에 쓰면
 * 그만큼 손이 늦는다. 외우기·수색과 달리 바꾸기는 볼 것이 아니라 할 것이 있는 페이즈다.
 *
 * ## 수색 자막 뒤에 동사별 개수를 읊는다 (M4, `config.revealVerbCounts`)
 *
 * "생김 2, 없어짐 3"은 호스트가 뷰에 실어 준 개수(`view.verbCounts` - 켜져 있을 때만, 수색부터)를 그대로 읊는 것이다. 무엇이
 * 어디인지는 없고 개수만이고, M11부터는 **내가 찾을 수 있는 것**만 센다(합계가 HUD 분모와 같다 - 내 구획의 동사는
 * 내가 이미 아는 것이라 전체를 읊으면 뺄셈 하나로 남의 몫이 복원된다). 어휘는 단계 안내 화면과 같은 `verb.*`다. 자막(`누가 다녀갔다!`)이
 * 걷힌 **뒤에** 띄우는 이유는 둘이 겹치면 둘 다 안 읽히기 때문이고, 그 시점에 아직 수색이면(전량 발견으로 이미 리빌이면 말 것이
 * 없다) 토스트 하나로 끝난다. 세는 것은 호스트다 - 여기서 `changes`를 다시 세지 않는다(그 목록은 술래에게 없다).
 */

import { prefersReducedMotion } from '../core/env'
import { proxiedBetween, type Catalog } from '../core/room'
import type { RoomState, Verb } from '../core/types'
import { t } from '../i18n'
import { zoneName } from '../i18n/zoneName'
import { resetHitProxies, syncHitProxies } from '../render/hitProxy'
import type { SceneCtx } from '../render/scene'
import type { RoomTransition } from '../render/transition'
import type { GameHudHandle } from '../ui/gameHud'
import type { JuiceHandle } from '../ui/juice'
import type { RevealPartyHandle } from '../ui/revealParty'
import { VERB_ORDER, type ClientView } from './match/matchView'
import type { MatchPhase } from './match/matchTypes'
import { partyRevealSequence } from './matchSessionReveal'
import type { MatchRooms } from './matchSessionScene'
import { buildMatchResultView } from './matchSessionView'
import type { SessionAudio } from './sessionAudio'
import { huntStampDelayMs } from './sessionEffects'
import type { SessionResult } from './sessionResult'
import type { SessionReveal } from './sessionReveal'
import { missedFrom } from './sessionView'
import type { WarpVeil } from './sessionWarp'
import type { Tuning } from './tuning'

/* --- 타입 ------------------------------------------------------------------------ */

/**
 * 페이즈 처리가 세션에서 빌려 쓰는 것 전부. 효과 번역기(`MatchEffectParts`)가 이것을 **포함**한다 - 세션은 인자 묶음 하나를
 * 만들고, 효과 번역기가 그 묶음을 그대로 여기에 넘긴다(같은 물건을 두 번 배선하지 않는다).
 */
export type MatchPhaseParts = Readonly<{
  tuning: Tuning
  catalog: Catalog
  ctx: SceneCtx
  hud: GameHudHandle
  juice: JuiceHandle
  audio: SessionAudio
  reveal: SessionReveal
  /** 파티 리빌의 이름표(M5). 스텝 진입은 세션이 `reveal.onStepEnter`로 배선하고, 여기는 리빌이 접힐 때 함께 걷는다. */
  revealParty: RevealPartyHandle
  result: SessionResult
  transition: RoomTransition
  warpVeil: WarpVeil
  /** 지금 뷰. 효과가 적용되는 시점에는 이미 그 `step`의 새 뷰다(리듀서가 올려 둔 값을 읽는 싱글과 같다). */
  view: () => ClientView
  rooms: () => MatchRooms
  showRoom: (toOrigin: boolean) => void
  resetCamera: () => void
  /** 페이즈 자막이 떠 있는 동안 호스트 시계(루프백의 `transport.tick`)를 잡는다. 소켓에서는 아무 일도 없다. */
  holdClock: (held: boolean) => void
  /** 지금 호스트 시각의 추정(`matchClock.hostNowMs`). 수색 소요 시간은 이 값의 차다. */
  hostNowMs: () => number
  /** 세션이 깨어 있는가. 잠든 무대(배경)에서는 곡을 뽑지 않는다. */
  isActive: () => boolean
}>

export type MatchPhaseHandler = Readonly<{
  /** `phaseEnter` 효과 하나. 페이즈의 첫 화면을 세운다. */
  onPhaseEnter: (phase: MatchPhase, previous: MatchPhase) => void
  /**
   * 수색 진입의 방 교체를 전환이 가져갔다면 그 소유권을 **한 번** 넘겨받는다(참을 돌려주고 즉시 놓는다).
   * `warpTo`가 디졸브를 탈지 말지는 이 답 하나로 정한다 - 두 번째 호출부터는 항상 거짓이다(머리말).
   */
  consumeTransitionWarp: () => boolean
  /** 제거 프록시를 두 방의 차집합으로 다시 세운다(`syncProxies` 효과). */
  syncProxies: () => void
  /** 결과 화면을 (다시) 띄운다. 배치 모드에서 돌아올 때도 부른다. */
  showResult: () => void
  /** 리빌을 세운다. 뷰에 정답표가 있어야 한다(리빌부터 온다). */
  startReveal: () => void
  /** 진행 중인 전환을 접고 그 소유권도 놓는다. 라운드가 접히거나 세션이 잠들 때. */
  resetTransition: () => void
}>

/* --- 순수 조각 -------------------------------------------------------------------- */

/**
 * 동사별 개수 → 토스트 한 줄("이번엔 생김 2, 없어짐 3"). 0개인 동사는 적지 않고(없는 것을 읊으면 목록이 항상 다섯이라 눈이 안 간다),
 * 순서는 호스트가 세는 순서(`VERB_ORDER`) 그대로다. 전부 0이면(변화 0개 라운드는 없지만 방어) `null` - 빈 토스트를 띄우지 않는다.
 * 이음말은 `common.separator`다 - 목록을 잇는 자리는 전부 그 키를 쓴다(GLOSSARY 4.2).
 */
export function verbCountsText(counts: Readonly<Record<Verb, number>>): string | null {
  const items = VERB_ORDER.filter((verb) => counts[verb] > 0).map((verb) => t('verbCounts.item', { verb: t(`verb.${verb}`), count: counts[verb] }))
  if (items.length === 0) return null
  return t('verbCounts.notice', { list: items.join(t('common.separator')) })
}

/* --- 입구 ------------------------------------------------------------------------ */

export function createMatchPhaseHandler(parts: MatchPhaseParts): MatchPhaseHandler {
  const { tuning, catalog, ctx, hud, juice, audio, reveal, revealParty, result, transition, warpVeil } = parts

  /** 이번 수색 진입의 방 교체를 전환이 가져갔는가. 뒤따라오는 `warpTo` **한 번만** 소비한다(머리말). */
  let transitionOwnsWarp = false
  /** 수색이 시작된 호스트 시각. 소요 시간 = 수색을 떠나는 `step`의 시각 - 이 값. */
  let huntStartedAtMs = 0
  let huntElapsedMs = 0

  /* --- 프록시 --- */

  /**
   * 프록시의 재료 - 제거된 자리와 **옮기기 전 자리** 둘이다(`render/hitProxy.ts` 머리말). 찾는 사람에게는
   * 정답표가 없으므로(수색 중 `changes: null`) 두 방의 대조로 세운다(`core/room.proxiedBetween` -
   * 새로 새는 정보는 없다, 11.2절). 방이 아직 없으면 세울 것도 없다.
   */
  function proxyRooms(): Readonly<{ original: RoomState; mutated: RoomState }> | null {
    const rooms = parts.rooms()
    if (rooms.original === null) return null
    return { original: rooms.original, mutated: rooms.mutated ?? rooms.original }
  }

  function syncProxies(): void {
    const rooms = proxyRooms()
    if (rooms === null) return
    syncHitProxies(ctx.scene, rooms.original, rooms.mutated, proxiedBetween(rooms.original, rooms.mutated), catalog)
  }

  /* --- 리빌 · 결과 --- */

  function startReveal(): void {
    const view = parts.view()
    const rooms = proxyRooms()
    // 정답표는 리빌부터 뷰에 온다(`matchView.changesFor`). 없으면 세울 것이 없다 - 뷰가 바뀌면 다시 들어온다.
    if (rooms === null || view.changes === null) return
    /*
     * 수색이 0.6초 안에 끝나는 경로가 있다(전량 발견). 전환이 화면을 덮은 채로 리빌이 시작되면 깜빡 교차가 통째로 가려진다.
     * `finish()`는 대기 중인 방 교체를 반드시 실행하므로 여기서 접어도 수색이 원본 방에 갇히지 않는다.
     */
    resetTransition()
    warpVeil.cancel()
    // 씬은 여기서부터 리빌이 끝날 때까지 **변경본으로 고정**이다(`sessionReveal.ts` 머리말).
    parts.showRoom(false)
    /*
     * 파티 리빌(M5). 같은 재생기에 순서 옵션만 다르다 - 마지막 자리는 가장 쉬웠는데 다 놓친 것(`'easiest'`)이고 스텝마다
     * 찾은 사람(`found[].by`)이 붙는다(`matchSessionReveal.partyRevealSequence`). 싱글은 이 인자를 넘기지 않아 그대로다.
     */
    reveal.start(
      { original: rooms.original, mutated: rooms.mutated, changes: view.changes, found: view.found },
      prefersReducedMotion(),
      partyRevealSequence(view.found),
    )
  }

  function showResult(): void {
    // 리빌 도중 접힌 경로에서 금빛이 남으면 결과 화면 배경에 정답 하나가 계속 표시된다(`session.showResult`와 같은 줄).
    reveal.clearHighlight()
    // 이름표도 같다 - 마지막 스텝의 "OO이 찾았다"가 결과 화면 위에 남으면 순위표와 두 겹으로 말한다.
    revealParty.clear()
    parts.showRoom(false)
    result.showView(buildMatchResultView(parts.view(), huntElapsedMs, missedFrom(reveal.steps())))
  }

  function resetTransition(): void {
    transition.finish()
    transitionOwnsWarp = false
  }

  function consumeTransitionWarp(): boolean {
    if (!transitionOwnsWarp) return false
    transitionOwnsWarp = false
    return true
  }

  /* --- 페이즈 --- */

  /**
   * 자막 한 장과 그동안의 시계 잠금. `onDone`은 반드시 한 번 불린다(`PhaseStampOptions.onDone`의 계약).
   * `after`는 자막이 걷힌 뒤 시계를 푼 다음에 한 번 - 수색의 동사별 개수 토스트가 여기 탄다(머리말).
   */
  function stamp(which: 'watch' | 'hunt', delayMs = 0, after?: () => void): void {
    parts.holdClock(true)
    juice.phase(which, {
      delayMs,
      onDone: () => {
        parts.holdClock(false)
        after?.()
      },
    })
  }

  /**
   * 바꾸기 진입의 한 줄 - "거실이 네 구획이다"(M11). 이름은 호스트가 배정한 구획 id로 사전을 조회한 것이고
   * (`i18n/zoneName.ts`) 좌표에서 구획을 되짚지 않는다. 구획을 못 받은 판(좌석보다 구획이 적다 - 있으면 안 되는
   * 자리다)은 그 사실을 말한다: 아무 말 없이 도구가 안 서면 화면이 고장으로 읽힌다(GLOSSARY 3절).
   * 자막(`juice.phase`)을 쓰지 않는 이유는 그것이 시계를 잡기 때문이다 - 바꾸기 60초는 첫 1초부터 손이 움직여야 한다.
   */
  function announceZone(): void {
    const name = zoneName(parts.view().myZone?.zoneId)
    hud.showNotice(name === undefined ? t('zone.none') : t('zone.assigned', { name }), 'good')
  }

  /** 수색 자막이 걷힌 뒤. 아직 수색이고 호스트가 개수를 실어 줬을 때만 한 줄(머리말). */
  function announceVerbCounts(): void {
    const view = parts.view()
    if (view.phase !== 'hunt' || view.verbCounts === null) return
    const text = verbCountsText(view.verbCounts)
    if (text !== null) hud.showNotice(text, 'info')
  }

  /**
   * 새 라운드의 **첫 페이즈**인가. 무엇이 첫 페이즈인지는 규칙이 정한다 - 꾸미기(M10)일 수도, 바꾸기일 수도,
   * 손댈 사람이 없으면 외우기일 수도 있다(`matchPhases.startRound`). 그래서 페이즈 이름이 아니라 **어디서 왔는가**로 묻는다.
   *
   * 한때 이 청소는 `case 'watch'` 안에 있었다. 외우기가 라운드의 첫 페이즈이던 시절의 자리이고, 3절 개정으로
   * 바꾸기가 앞으로 오면서 **지난 라운드의 리빌 판·결과 판·프록시가 바꾸기 60초 내내 남게 됐다.** 프록시가 남으면
   * 그 유령이 체인저의 물건 고르기를 가로챈다(탭이 `removed`로 해석된다).
   */
  function startsRound(previous: MatchPhase): boolean {
    return previous === 'lobby' || previous === 'result'
  }

  /** 지난 라운드의 흔적을 통째로 걷는다. 라운드의 첫 페이즈에서 **한 번만** 돈다. */
  function openRound(): void {
    reveal.clear()
    revealParty.clear()
    result.hide()
    // 지난 라운드의 자막을 먼저 걷는다 - 순서를 뒤집으면 방금 건 예약을 `clear()`가 취소한다.
    juice.clear()
    parts.resetCamera()
    // 프록시를 통째로 버린다. 라운드가 같은 방을 재사용하므로 지난 라운드의 제거 자리가 그대로 남는다(`session.ts`와 같은 이유).
    const rooms = proxyRooms()
    if (rooms !== null) {
      resetHitProxies(ctx.scene, rooms.original, rooms.mutated, proxiedBetween(rooms.original, rooms.mutated), catalog)
    }
    // 새 판 = 새 곡(같은 방 + 같은 곡이면 두 번째 판이 첫 판의 재생처럼 들린다). 잠든 무대에서는 부르지 않는다.
    if (parts.isActive()) audio.startBgm()
  }

  function onPhaseEnter(phase: MatchPhase, previous: MatchPhase): void {
    // 수색을 떠나는 순간의 경과. 호스트 시각의 차다 - 프레임 누적이 아니라 `step.atMs`의 것이다.
    if (previous === 'hunt') huntElapsedMs = Math.max(0, parts.hostNowMs() - huntStartedAtMs)
    if (startsRound(previous)) openRound()

    switch (phase) {
      case 'lobby':
      case 'decorate':
        return
      case 'watch': {
        stamp('watch')
        return
      }
      case 'change':
        // 전원이 자기 구획을 고친다(M11). 화면은 원본 그대로이고 바뀌는 것은 **손이 닿는 범위**뿐이라, 그 범위를
        // 첫 문장이 말한다. 도구·구획 테두리를 세우는 것은 무대(`app/stageChanger.ts`)이고 여기는 말만 얹는다.
        announceZone()
        return
      case 'hunt':
        reveal.stop()
        revealParty.clear()
        huntStartedAtMs = parts.hostNowMs()
        /*
         * 지금 씬은 아직 원본이다 - 교체는 뒤따르는 `warpTo`가 한다(`enterHunt`의 효과 순서). 그 프레임을 굳혀 왜곡을 돌리고
         * 화면이 100% 먹색인 순간 방을 갈아 끼운다. 이펙트가 서지 못하면(`false`) 아래 `warpTo`의 디졸브가 대신 한다.
         */
        transitionOwnsWarp = transition.play(() => parts.showRoom(false))
        // 화면을 덮고 있는 연출의 실제 길이. 싱글과 같은 함수다(`sessionEffects.huntStampDelayMs`).
        stamp('hunt', huntStampDelayMs(transitionOwnsWarp, tuning.warpDissolveSeconds, prefersReducedMotion()), announceVerbCounts)
        return
      case 'reveal':
        startReveal()
        return
      case 'result':
        reveal.stop()
        showResult()
        return
    }
  }

  return { onPhaseEnter, consumeTransitionWarp, syncProxies, showResult, startReveal, resetTransition }
}
